/**
 * World transition between the shop and the Ralph & Doug world (collection template
 * `ralph-and-doug`). Markup, styles and the pre-paint arrival gate live in
 * snippets/rd-world-transition.liquid; this file does the moving parts.
 *
 * A click on a link that crosses the border plays the first half on the current page: a
 * marker stroke scribbles over the screen and swells until it covers everything (Ralph
 * Blue into the R&D world, with Ralph chasing Doug; black back into the shop). Then a
 * handoff note goes into sessionStorage and the browser navigates. The next page finds the
 * note in its pre-paint gate, starts fully covered and plays the second half: the stroke
 * pulls back and uncovers the new world.
 *
 * Deliberately left alone: the buying flow (/products, /cart, /checkout, /account), links
 * opened in a new tab, downloads, pure anchors, modified clicks and reduced motion. A
 * visitor who lands on the R&D page directly (search, ads) sees no transition either,
 * there is no handoff.
 *
 * If settings.show_page_transition (native cross-document View Transitions) is ever
 * switched on, these navigations should skip it (pageswap -> viewTransition.skipTransition),
 * or the browser slides its snapshot of the stroke on top of the stroke.
 *
 * Ported from the mockup's DrawSVG transition, written with requestAnimationFrame so no
 * animation library is needed.
 */
(function () {
  'use strict';

  var KEY = 'rd:world-handoff';
  var FRESH_MS = 4000;
  var STUCK_MS = 8000; // the covered page uncovers itself if the next page has not come by then
  var PHASE_MS = 900;
  var HOLD_MS = 60;
  var RUN_MS = 1300;
  var WIDTH_THIN = 2;
  var WIDTH_FULL = 300;
  // The buying flow and system pages never get the transition, whichever world they sit in.
  var EXCLUDED_PATH = /^\/(products|cart|checkout|checkouts|account|discount|apps|tools|policies)(\/|$)/;
  // Shopify locale and market prefixes like /en or /de-ch in front of the real path.
  var LOCALE_PREFIX = /^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i;

  var easeInOut = function (t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; };
  var lerp = function (a, b, t) { return a + (b - a) * t; };

  function normalisePath(pathname) {
    var p = String(pathname || '/').replace(LOCALE_PREFIX, '');
    if (p.length > 1) p = p.replace(/\/+$/, '');
    // A product reached through a collection is the same page as its plain /products/ URL.
    p = p.replace(/^\/collections\/[^/]+(\/products\/)/, '$1');
    return p || '/';
  }

  /** 'rd' or 'shop' for a URL, from the R&D collection paths the layout publishes. */
  function worldOf(url, cfg) {
    // ?view=ralph-and-doug (the collection) and ?view=rd-ralph etc. (the product templates).
    var view = url.searchParams ? url.searchParams.get('view') : null;
    var views = cfg.views || (cfg.view ? [cfg.view] : []);
    for (var v = 0; view && v < views.length; v++) {
      if (view.indexOf(views[v]) === 0) return 'rd';
    }
    var path = normalisePath(url.pathname);
    var paths = cfg.rdPaths || [];
    for (var i = 0; i < paths.length; i++) {
      if (normalisePath(paths[i]) === path) return 'rd';
    }
    return 'shop';
  }

  /**
   * The world a click leads into, or null when the transition should not play.
   * `anchor` needs getAttribute/hasAttribute/href/target, `event` the usual click fields.
   */
  function targetWorld(anchor, event, loc, cfg) {
    if (!anchor || !event) return null;
    if (event.defaultPrevented) return null;
    if (event.button !== undefined && event.button !== 0) return null;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
    if (anchor.hasAttribute('download')) return null;
    if (anchor.target && anchor.target !== '_self') return null;

    var href = anchor.getAttribute('href');
    if (!href || href.charAt(0) === '#' || /^(mailto|tel|javascript):/i.test(href)) return null;

    var url;
    try { url = new URL(href, loc.href); } catch (e) { return null; }
    if (url.origin !== loc.origin) return null;
    var here = new URL(loc.href);
    if (url.pathname === here.pathname && url.search === here.search) return null;
    if (EXCLUDED_PATH.test(normalisePath(url.pathname))) return null;

    var explicit = anchor.getAttribute('data-rd-world');
    var world = explicit === 'rd' || explicit === 'shop' ? explicit : worldOf(url, cfg);
    return world !== cfg.world ? world : null;
  }

  /** The handoff note if it is fresh and meant for this page, else null. */
  function readHandoff(raw, now, here) {
    if (!raw) return null;
    var note;
    try { note = JSON.parse(raw); } catch (e) { return null; }
    if (!note || typeof note.at !== 'number' || typeof note.to !== 'string') return null;
    var age = now - note.at;
    if (age < 0 || age > FRESH_MS) return null;
    if (here && note.to !== here) return null;
    return note;
  }

  var api = {
    KEY: KEY,
    FRESH_MS: FRESH_MS,
    normalisePath: normalisePath,
    worldOf: worldOf,
    targetWorld: targetWorld,
    readHandoff: readHandoff
  };
  window.RdWorldTransition = api;

  var doc = window.document;
  if (!doc || !doc.getElementById) return;
  var overlay = doc.querySelector('[data-rd-wt]');
  var configEl = doc.getElementById('rd-wt-config');
  if (!overlay || !configEl) return;

  var cfg;
  try { cfg = JSON.parse(configEl.textContent); } catch (e) { return; }
  var root = doc.documentElement;
  var stage = overlay.querySelector('.rd-wt__stage');
  var svg = overlay.querySelector('.rd-wt__svg');
  var turn = overlay.querySelector('.rd-wt__turn');
  var path = overlay.querySelector('.rd-wt__path');
  var ralph = overlay.querySelector('.rd-wt__runner--ralph');
  var doug = overlay.querySelector('.rd-wt__runner--doug');
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var busy = false;

  // Visible stroke segment from s to e (0..1 of the path length), like drawSVG "s% e%".
  var draw = function (s, e, width) {
    path.style.visibility = e - s > 0 || width > WIDTH_THIN ? 'visible' : 'hidden';
    path.style.strokeDasharray = (e - s) + ' 3';
    path.style.strokeDashoffset = String(-s);
    path.style.strokeWidth = String(width);
  };

  var tween = function (duration, onUpdate) {
    return new Promise(function (resolve) {
      var start = null;
      var step = function (now) {
        if (start === null) start = now;
        var t = Math.min((now - start) / duration, 1);
        onUpdate(easeInOut(t));
        if (t < 1) window.requestAnimationFrame(step); else resolve();
      };
      window.requestAnimationFrame(step);
    });
  };
  var wait = function (ms) { return new Promise(function (r) { window.setTimeout(r, ms); }); };

  // The scribble is drawn for landscape (1316 x 664). Portrait screens turn it 90 degrees so
  // it runs top to bottom; turned, its aspect (0.5) almost matches a phone.
  var fitOrientation = function () {
    var portrait = window.innerHeight > window.innerWidth;
    svg.setAttribute('viewBox', portrait ? '0 0 664 1316' : '0 0 1316 664');
    if (portrait) turn.setAttribute('transform', 'translate(664 0) rotate(90)');
    else turn.removeAttribute('transform');
  };

  // Runner images load on intent (hover, touch, focus on a link into the R&D world), never
  // on a plain page view.
  var runnersLoaded = false;
  var loadRunners = function () {
    if (runnersLoaded || !ralph) return;
    runnersLoaded = true;
    Array.prototype.forEach.call(overlay.querySelectorAll('img[data-src]'), function (img) {
      img.src = img.getAttribute('data-src');
      img.removeAttribute('data-src');
    });
  };

  // Ralph chases Doug on a straight line, independent of the stroke.
  //
  // Measured once per run, moved through `translate` (custom properties, see the snippet's
  // CSS). The first version wrote left/top and then read offsetWidth in every frame, which
  // forced a style and layout pass each time: 6 to 9 ms per frame at 6x CPU throttling,
  // half the budget of a 60 fps frame (measured 2026-10-14).
  var measureRunners = function () {
    var dw = doug.offsetWidth, rw = ralph.offsetWidth;
    if (!dw || !rw) return null; // images not decoded yet: try again next frame
    return { w: window.innerWidth, y: window.innerHeight * 0.7, dw: dw, rw: rw, trail: dw * 0.5 + rw * 0.5 + 24 };
  };
  var placeRunners = function (t, geo) {
    var x = lerp(-geo.dw / 2, geo.w + geo.rw / 2 + geo.trail, t);
    doug.style.setProperty('--rd-run-x', x + 'px');
    ralph.style.setProperty('--rd-run-x', (x - geo.trail) + 'px');
  };
  var showRunners = function (geo) {
    doug.style.setProperty('--rd-run-y', geo.y + 'px');
    ralph.style.setProperty('--rd-run-y', geo.y + 'px');
    doug.style.visibility = ralph.style.visibility = 'visible';
  };
  var hideRunners = function () {
    if (ralph) ralph.style.visibility = 'hidden';
    if (doug) doug.style.visibility = 'hidden';
  };
  var runChase = function () {
    var start = null;
    var geo = null;
    var step = function (now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / RUN_MS, 1);
      if (!geo) {
        geo = measureRunners();
        if (geo) {
          placeRunners(t, geo);
          showRunners(geo);
        }
      } else {
        placeRunners(t, geo);
      }
      if (t < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  };

  var setColour = function (to) {
    root.classList.remove('rd-wt-to-rd', 'rd-wt-to-shop');
    root.classList.add('rd-wt-to-' + to);
  };

  var reset = function () {
    root.classList.remove('rd-wt-run', 'rd-wt-covered', 'rd-wt-to-rd', 'rd-wt-to-shop');
    stage.style.opacity = '';
    overlay.style.opacity = '';
    draw(0, 0, WIDTH_THIN);
    hideRunners();
    busy = false;
  };

  // First half, on the page being left: scribble in and cover.
  var leave = function (to) {
    fitOrientation();
    setColour(to);
    root.classList.add('rd-wt-run');
    stage.style.opacity = '1';
    overlay.style.opacity = '1';
    draw(0, 0, WIDTH_THIN);
    if (to === 'rd' && ralph && doug) runChase();
    return tween(PHASE_MS, function (t) { draw(0, t, lerp(WIDTH_THIN, WIDTH_FULL, t)); })
      .then(function () { stage.style.opacity = '0'; return wait(HOLD_MS); });
  };

  // Second half, on the page arrived at: pull the stroke back and uncover.
  var enter = function () {
    fitOrientation();
    root.classList.add('rd-wt-run');
    overlay.style.opacity = '1';
    draw(0, 1, WIDTH_FULL);
    stage.style.opacity = '0';
    root.classList.remove('rd-wt-covered');
    return tween(PHASE_MS, function (t) {
      draw(t, 1, lerp(WIDTH_FULL, WIDTH_THIN, t));
      // The last third fades the overlay, so the thinning tail dissolves instead of ending as a dot.
      overlay.style.opacity = String(t < 2 / 3 ? 1 : 1 - (t - 2 / 3) * 3);
    }).then(reset);
  };

  doc.addEventListener('click', function (event) {
    if (busy || reduce) return;
    var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    var to = targetWorld(anchor, event, window.location, cfg);
    if (!to) return;
    event.preventDefault();
    busy = true;
    if (to === 'rd') loadRunners();
    var href = anchor.href;
    var go = function () {
      try { window.sessionStorage.setItem(KEY, JSON.stringify({ to: to, at: Date.now() })); } catch (e) { /* storage blocked: the next page simply shows without the second half */ }
      window.location.assign(href);
      // A navigation that never commits (stopped, offline, a download) must not leave this page
      // covered; a slow one shows the old page again while the new one is still loading.
      window.setTimeout(reset, STUCK_MS);
    };
    // Should the animation fail, the link still has to work.
    leave(to).then(go, function () {
      reset();
      window.location.assign(href);
    });
  });

  // Preload the runners as soon as a link into the R&D world is about to be used.
  var intent = function (event) {
    if (runnersLoaded || cfg.world === 'rd') return;
    var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor) return;
    if (targetWorld(anchor, { button: 0 }, window.location, cfg) === 'rd') loadRunners();
  };
  doc.addEventListener('pointerover', intent, { passive: true });
  doc.addEventListener('touchstart', intent, { passive: true });
  doc.addEventListener('focusin', intent);

  // Back/forward from the bfcache restores the page as it was left: still covered.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) reset();
  });

  if (root.classList.contains('rd-wt-covered')) {
    if (reduce) { reset(); return; }
    window.requestAnimationFrame(function () { enter(); });
  }
})();
